"use client";

import type {
  BeforeSendFn,
  Properties,
  SessionRecordingOptions,
} from "posthog-js";
import posthog from "posthog-js";

import { stripStripeReturnParamsFromUrl } from "@ergon-ai/utils";

import { runReadyCallback } from "../analytics/ready-queue";
import { DEFAULT_POSTHOG_HOST } from "./host";

/**
 * PostHog Client Configuration for Web Apps
 *
 * Centralized PostHog setup for Next.js applications.
 * Gracefully degrades when API key/host are missing.
 *
 * Usage in Next.js apps:
 * ```ts
 * import { initializePostHog } from "@ergon-ai/utils-web/posthog/client";
 *
 * // In a client component or layout
 * initializePostHog({
 *   apiKey: process.env.NEXT_PUBLIC_POSTHOG_API_KEY,
 *   apiHost: process.env.NEXT_PUBLIC_POSTHOG_API_HOST,
 *   debug: process.env.NODE_ENV === "development",
 * });
 * ```
 */

/**
 * Which properties carry a whole href, decided by NAME rather than by a list.
 *
 * A hand-kept list was the first shape and it was wrong: `getSessionProps()`
 * (`posthog-js/dist/module.full.js`) builds its keys by prefixing, so it emits
 * `$session_entry_url` and `$session_entry_referrer`, neither of which any list
 * written against today's SDK would contain. The SDK is free to add more the
 * next time it is bumped. Matching the name means a new one is covered the day
 * it appears instead of the day someone notices.
 */
const URL_PROPERTY_NAME = /url|referrer/i;

/**
 * One pass over this event's own keys. Values that are not strings and strings
 * that are not URLs are left exactly as they were, so a false match costs
 * nothing but the test.
 */
function stripUrlProperties(properties: Properties | undefined): void {
  if (!properties) return;
  for (const name of Object.keys(properties)) {
    if (!URL_PROPERTY_NAME.test(name)) continue;
    const value: unknown = properties[name];
    if (typeof value === "string") {
      properties[name] = stripStripeReturnParamsFromUrl(value);
    }
  }
}

/**
 * Stripe's return parameters leave no CAPTURED event, whoever composed it.
 * The SDK's own events read `location.href` when they fire, and two properties
 * outlive the page: `$initial_current_url` stays on the person, and
 * `$session_entry_url` rides every event of the session (EAH-2524).
 *
 * NOT session replay: rrweb writes the raw href inside `$snapshot_data`, which
 * this never reaches. That is covered only by `isExcludedFromReplay` in
 * `apps/web-professional/src/features/analytics/replay.ts`, a separate file that
 * has to stay in step with every return URL.
 */
const stripStripeReturnParams: BeforeSendFn = (event) => {
  stripUrlProperties(event.properties);
  stripUrlProperties(event.properties.$set as Properties | undefined);
  stripUrlProperties(event.properties.$set_once as Properties | undefined);
  stripUrlProperties(event.$set);
  stripUrlProperties(event.$set_once);
  return event;
};

export interface PostHogConfig {
  apiKey?: string;
  apiHost?: string;
  debug?: boolean;
  /** `<app>@<commit>` — the same string the build hands Sentry. */
  release?: string;
  appVersion?: string;
  /**
   * Replay privacy, passed straight to posthog-js `session_recording`.
   *
   * Left out on purpose by the marketing sites, which is what posthog-js
   * defaults mean: inputs are masked, TEXT IS NOT. An app whose screens carry
   * someone else's data passes `{ maskAllInputs: true, maskTextSelector: "*" }`.
   * Set here rather than in the PostHog project because the SDK gives the init
   * options precedence over the project's "Privacy and masking" setting
   * (lazy-loaded-session-recorder.js, `_masking`), so a project setting cannot
   * be relied on to mask an app that ships its own.
   */
  sessionRecording?: SessionRecordingOptions;
  /**
   * Keep autocapture from reading element `textContent`. Separate from replay:
   * masking the recording does nothing to the `$autocapture` events.
   */
  maskAllText?: boolean;
  /**
   * Start with replay off, for an app whose current page must never be recorded.
   *
   * This is the ONLY reliable way to keep a page out of a recording, and calling
   * `stopSessionRecording()` after init is not a substitute. `init` starts the
   * recorder synchronously when the project's replay config is already in
   * localStorage (a returning visitor), and `_onScriptLoaded` then calls
   * `start()` without re-reading `disable_session_recording` — the lazily
   * loaded recorder never reads that flag at all (0 references in
   * lazy-loaded-session-recorder.js). So a stop issued after `init` returns
   * hits a recorder that does not exist yet, and the script starts anyway.
   */
  disableSessionRecording?: boolean;
}

type PostHogReadyCallback = (client: typeof posthog) => void;

/** One tag for every failure out of this queue. */
const READY_QUEUE_SOURCE = "posthog_ready_queue";

/** Queued until init; null once flushed (from then on callbacks run at once). */
let pendingReadyCallbacks: PostHogReadyCallback[] | null = [];

/**
 * Run `callback` as soon as posthog.init has been called — immediately if it
 * already was. posthog-js DROPS anything captured before init (its source
 * calls uninitializedWarning — there is no queue), and React runs child
 * effects before parent effects, so a component that captures in its mount
 * effect always beats the provider effect that calls init. Any capture that
 * must survive that window goes through here. When PostHog is never
 * configured (no API key), the callbacks never run: analytics are off.
 */
export function whenPostHogReady(callback: PostHogReadyCallback): void {
  if (pendingReadyCallbacks === null) {
    runReadyCallback(callback, posthog, READY_QUEUE_SOURCE);
    return;
  }
  pendingReadyCallbacks.push(callback);
}

/**
 * Initialize PostHog client for browser environments
 */
export function initializePostHog(config: PostHogConfig): typeof posthog {
  const {
    apiKey,
    apiHost,
    debug = false,
    release,
    appVersion,
    sessionRecording,
    maskAllText,
    disableSessionRecording,
  } = config;

  // Gracefully degrade when PostHog is not configured
  if (typeof window === "undefined") {
    if (debug) {
      console.warn("⚠️ PostHog initialization skipped (server-side)");
    }
    return posthog;
  }

  if (!apiKey) {
    if (debug) {
      console.warn("⚠️ PostHog not configured - analytics disabled", {
        hasApiKey: !!apiKey,
        hasApiHost: !!apiHost,
      });
    }
    return posthog;
  }

  posthog.init(apiKey, {
    api_host: apiHost ?? DEFAULT_POSTHOG_HOST,
    // ui_host keeps SDK-generated links (replay, toolbar) on PostHog, not the proxy
    ui_host: DEFAULT_POSTHOG_HOST,
    // Lazy-loaded extras (remote config, web-vitals, surveys) go to <head>.
    // The default splices them before the first <script> in the body — inside
    // DOM React is still hydrating on streamed pages, which shifts sibling
    // positions and fails hydration, regenerating the whole tree client-side.
    external_scripts_inject_target: "head",
    // Spread rather than a plain key. posthog-js merges init config with an
    // `extend` that skips undefined, so a bare `session_recording: undefined`
    // would in fact be harmless — but `lazy-loaded-session-recorder.js` reads
    // `config.session_recording.session_idle_threshold_ms` with no optional
    // chaining, so an app that asks for nothing is safest sending nothing.
    ...(sessionRecording ? { session_recording: sessionRecording } : {}),
    ...(maskAllText === undefined ? {} : { mask_all_text: maskAllText }),
    ...(disableSessionRecording === undefined
      ? {}
      : { disable_session_recording: disableSessionRecording }),
    debug,
    capture_pageview: false, // Manual page view tracking
    before_send: stripStripeReturnParams,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    // posthog-js defaults this false, so opt_out_capturing() would stop
    // sending but leave persistence on — and reset() mints a new distinct_id
    // straight back into a fresh cookie. Withdrawal would hand the visitor a
    // brand new identifier.
    opt_out_persistence_by_default: true,
    loaded: (posthog) => {
      if (debug) posthog.debug();
    },
  });

  // Super properties, so every event says which deploy produced it. Registered
  // before the waiters flush, or the first queued capture ships without them.
  if (release)
    posthog.register({ app_release: release, app_version: appVersion });

  // init() is synchronous about what matters here (persistence, distinct_id,
  // the internal capture queue), so the waiters can run now — no need to wait
  // for the remote script/config.
  const queued = pendingReadyCallbacks ?? [];
  pendingReadyCallbacks = null;
  for (const callback of queued) {
    runReadyCallback(callback, posthog, READY_QUEUE_SOURCE);
  }

  return posthog;
}

// Export the posthog instance
export { posthog };
